import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Typography,
  Stack,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
} from '@mui/icons-material';

interface AudioPlayerProps {
  audioUrl: string;
  title: string;
  disabled?: boolean;
  onAudioReady?: (audioElement: HTMLAudioElement, audioContext: AudioContext, volume: number) => void;
  onVolumeChange?: (volume: number) => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl, title, disabled = false, onAudioReady, onVolumeChange }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const previousVolumeRef = useRef<number>(1); // Store volume before muting

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Create AudioContext for Web Audio API
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    const setAudioData = () => {
      setDuration(audio.duration);
      setCurrentTime(audio.currentTime);

      // Notify parent when audio is ready
      if (onAudioReady && audioContextRef.current) {
        onAudioReady(audio, audioContextRef.current, volume);
      }
    };

    const setAudioTime = () => setCurrentTime(audio.currentTime);

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadeddata', setAudioData);
    audio.addEventListener('timeupdate', setAudioTime);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadeddata', setAudioData);
      audio.removeEventListener('timeupdate', setAudioTime);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioUrl]); // Removed onAudioReady from deps to prevent recreating AudioContext

  // Separate effect to sync volume state with audio element
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume;
    }
  }, [volume]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    // Resume AudioContext if suspended (required by browsers)
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (_event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (!audio || Array.isArray(newValue)) return;

    audio.currentTime = newValue;
    setCurrentTime(newValue);
  };

  const handleVolumeChange = (_event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (!audio || Array.isArray(newValue)) return;

    const newVolume = newValue / 100;
    audio.volume = newVolume;
    setVolume(newVolume);

    // Notify parent of volume change
    if (onVolumeChange) {
      onVolumeChange(newVolume);
    }

    // Update previous volume if not muted and volume > 0
    if (newVolume > 0) {
      previousVolumeRef.current = newVolume;
    }

    // Automatically unmute if user drags slider from 0
    if (isMuted && newVolume > 0) {
      setIsMuted(false);
    }

    // Automatically mute if user drags slider to 0
    if (newVolume === 0) {
      setIsMuted(true);
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      // Unmute: restore previous volume (or default to 1 if somehow 0)
      const restoreVolume = previousVolumeRef.current > 0 ? previousVolumeRef.current : 1;
      audio.volume = restoreVolume;
      setVolume(restoreVolume);
      setIsMuted(false);
      if (onVolumeChange) {
        onVolumeChange(restoreVolume);
      }
    } else {
      // Mute: save current volume and set to 0
      previousVolumeRef.current = volume > 0 ? volume : 1;
      audio.volume = 0;
      setVolume(0);
      setIsMuted(true);
      if (onVolumeChange) {
        onVolumeChange(0);
      }
    }
  };

  const formatTime = (time: number): string => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{
      p: { xs: 1.5, sm: 2 },
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      backgroundColor: 'background.paper',
      opacity: disabled ? 0.6 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
      overflow: 'hidden'
    }}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" crossOrigin="anonymous" />

      <Typography variant="subtitle2" gutterBottom noWrap sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
        {title}
      </Typography>

      <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1 }} sx={{ width: '100%' }}>
        {/* Play/Pause Button */}
        <IconButton
          onClick={togglePlayPause}
          disabled={disabled}
          size="small"
          color="primary"
          sx={{ flexShrink: 0 }}
        >
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>

        {/* Time Display */}
        <Typography variant="caption" sx={{ minWidth: { xs: '30px', sm: '35px' }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, flexShrink: 0 }}>
          {formatTime(currentTime)}
        </Typography>

        {/* Progress Slider */}
        <Slider
          value={currentTime}
          max={duration || 100}
          onChange={handleSeek}
          disabled={disabled}
          size="small"
          sx={{ flexGrow: 1, minWidth: 0, mx: { xs: 0.5, sm: 1 } }}
        />

        {/* Duration */}
        <Typography variant="caption" sx={{ minWidth: { xs: '30px', sm: '35px' }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, flexShrink: 0 }}>
          {formatTime(duration)}
        </Typography>

        {/* Volume Control */}
        <IconButton
          onClick={toggleMute}
          disabled={disabled}
          size="small"
          sx={{ flexShrink: 0 }}
        >
          {isMuted ? <VolumeOff /> : <VolumeUp />}
        </IconButton>

        <Slider
          value={isMuted ? 0 : volume * 100}
          onChange={handleVolumeChange}
          disabled={disabled}
          size="small"
          sx={{ width: { xs: 40, sm: 60 }, flexShrink: 0 }}
          max={100}
        />
      </Stack>
    </Box>
  );
};

export default AudioPlayer;